import { Injectable } from 'angular-ts-decorators';

import { CurrentSession } from './current-session';
import { Domain } from './domain';
import { Session } from './session';
import type { SessionStorage } from './session-storage';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SessionRequests } from './session-requests';

/**
 * Service that allows for changing the current session.
 */
@Injectable('sessionManager')
export class SessionManager {
  private SESSION_COOKIE: string;
  private TENANT_COOKIE: string;

  constructor(
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private currentSession: CurrentSession,
    private domain: Domain,
    private ipCookie,
    private sentry: Sentry,
    private session: Session,
    private sessionRequests: SessionRequests,
    private sessionStorage: SessionStorage,
  ) {
    'ngInject';

    this.SESSION_COOKIE = 'sid';
    this.TENANT_COOKIE = 'tenantId';
  }

  /**
   * Collect all SID cookie values from different domain levels
   * This is needed because SIDs might be set at both subdomain and root domain levels
   * @returns {string[]} Array of unique SID values found in cookies
   */
  private getAllSidCookies(): string[] {
    const sids: string[] = [];

    // Get SID from current domain (via ipCookie)
    const currentDomainSid = this.ipCookie(this.SESSION_COOKIE);
    if (currentDomainSid) {
      sids.push(currentDomainSid);
    }

    // Parse all cookies to find any 'sid' cookies that might be set on different domains
    const allCookies = this.$window.document.cookie.split(';');
    for (const cookie of allCookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.SESSION_COOKIE && value && !sids.includes(value)) {
        sids.push(value);
      }
    }

    return sids.filter((sid) => sid && sid.trim() !== '');
  }

  /**
   * Remove SID cookies from all possible domain levels
   * This ensures complete logout across all domains/subdomains
   */
  private removeAllSidCookies(): void {
    // Remove from current domain via ipCookie
    this.ipCookie.remove(this.SESSION_COOKIE, {
      domain: this.domain.getParent(),
      path: '/',
    });

    // Also try to remove from root domain directly via document.cookie
    const cookieDomain = this.getCookieRootDomain(
      this.$window.location.hostname,
    );
    if (cookieDomain) {
      // Set expired cookies to remove them
      this.$window.document.cookie = `${this.SESSION_COOKIE}=; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

      // Also try without the leading dot
      const rootDomain = cookieDomain.startsWith('.')
        ? cookieDomain.substring(1)
        : cookieDomain;
      this.$window.document.cookie = `${this.SESSION_COOKIE}=; domain=${rootDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

      // Try current subdomain specifically
      this.$window.document.cookie = `${this.SESSION_COOKIE}=; domain=${this.$window.location.hostname}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  /**
   * Helper method similar to session.ts getCookieRootDomain
   * A naive helper that attempts to derive a root domain from a hostname.
   */
  private getCookieRootDomain(hostname: string): string | undefined {
    if (!hostname) {
      return undefined;
    }
    const parts = hostname.split('.');
    // We only support 2 segments, e.g. 'mydomain.com'
    if (parts.length > 2) {
      return '.' + parts.slice(parts.length - 2).join('.');
    } else {
      return '.' + hostname;
    }
  }

  /**
   *
   * Attempts to login
   * Can deal with basic auth and 2FA
   *
   * @param {string} username
   * user id usually email
   * @param {string} password
   * password
   * @param {boolean} rememberMe
   * Boolean that indicates if user whats to be logged in longer then default 48h.
   * @returns {httpPromise} it will resolve with updated currentSession object or a 2FA token
   */
  login(
    username: string,
    password: string,
    rememberMe: boolean,
  ): ng.IPromise<any> {
    const loginDeferred = this.$q.defer();

    this.sessionRequests.login(username, password, rememberMe).then(
      (response) => {
        if (!response['2FAToken']) {
          this.session.init();
        }

        if (response['2FAToken']) {
          loginDeferred.resolve(response);
        } else {
          loginDeferred.resolve(this.currentSession);
        }
      },
      (error) => {
        // login failed
        loginDeferred.reject(error);
      },
    );

    return loginDeferred.promise;
  }

  /**
   *
   * Sends 2FA token and code to backend and starts sessionManager and returns session info.
   *
   * @param {string} token
   * the 2FA token returned earlier by login
   * @param {string} code
   * the login code from the 2FA authetication app
   * @returns {httpPromise} it will resolve with updated currentSession object
   */
  loginTwoFa(token: string, code: string): ng.IPromise<any> {
    const loginDeferred = this.$q.defer();

    this.sessionRequests
      .loginTwoFa(token, code)
      .then(() => this.session.init())
      .then(
        () => {
          loginDeferred.resolve(this.currentSession);
        },
        (error) => {
          loginDeferred.reject(error);
        },
      );

    return loginDeferred.promise;
  }

  /**
   *
   * Sets tenant and update the sessionManager information.
   *
   * @param {string} tenantId
   * Current user need to have a permission to work as the tenant.
   * @returns {httpPromise} it will resolve with /set-tenant response, or reject with
   * error
   */
  setTenant(tenantId: string): ng.IPromise<any> {
    return this.sessionRequests.setTenant(tenantId).then(
      (setTenantResponse) => {
        this.ipCookie(this.TENANT_COOKIE, tenantId, {
          domain: this.domain.getParent(),
          path: '/',
        });

        this.session.init();

        return setTenantResponse;
      },
      (error) => {
        this.logout();

        return this.$q.reject(error);
      },
    );
  }

  /**
   *
   * Remove the sessionManager cookie & request deleting the sessionManager on the server
   * @return {boolean}
   * true if it's redirecting the page, false otherwise
   */
  logout(): ng.IPromise<any> {
    // Collect all possible SID values from different domain levels
    const sids = this.getAllSidCookies();

    this.currentSession.empty();
    this.sessionStorage.clear();
    this.sentry.setUser();

    // Make logout calls for each SID found (parallel requests)
    const logoutPromises = sids.map((sid) => this.sessionRequests.logout(sid));

    // Wait for all logout calls to complete (or fail)
    return this.$q.all(logoutPromises).then(
      () => {
        this.removeAllSidCookies();
        this.ipCookie.remove(this.TENANT_COOKIE, {
          domain: this.domain.getParent(),
          path: '/',
        });

        if (this.session.redirectAnonymousToGate) {
          this.$window.location.href = this.domain.link('www');

          return true;
        }

        this.session.init(); // re init, if staying where it was

        return false;
      },
      // Clear cookies and session to remove the user from a limbo logout state
      (error) => {
        this.removeAllSidCookies();

        this.currentSession.empty();

        return this.$q.reject(error);
      },
    );
  }
}
